import axios from "axios";
import { useEffect, useState } from "react";
import { TabContext, TabPanel } from "@mui/lab";
import ScrollablePageTab from "../dynamicForm/components/ScrollablePageTab";
import { Box, Button, List, TextField } from "@mui/material";
import localize from "../../common/localize";
import NotificationItem from "../../components/common/NotificationItem";
import AddOrEditBasicEntity from "../../components/common/AddOrEditBasicEntity";
import { getClearInventoryMovement } from "../../common/EntitiesManagement";
import ModalBackground from "../../components/common/ModalBackground";
import AddInventoryMovement from "../inventory/InventoryMovement/AddInventoryMovement";
import useGlobalContext from "../../hooks/useGlobalContext";
import InventoryMovementItem from "../inventory/InventoryMovement/InventoryMovementItem";
import SupplyItem from "../supplies/components/SupplyItem";
import SupplyFilter from "../supplies/components/SupplyFilter";
import InventoryMovementFilter from "../inventory/InventoryMovement/InventoryMovementFilter";

export default function WarehouseDetail({
  model,
  setModel,
  setSuppliesForPrinting,
}) {
  const { WAREHOUSE_ENDPOINT, INVENTORY_MOVEMENT_HEADER_ENDPOINT, config } =
    useGlobalContext();
  const [selectedTab, setSelectedTab] = useState("0");
  const [supplies, setSupplies] = useState(null);
  const [inventoryMovements, setInventoryMovements] = useState(null);
  const [inventoryMovementsFiltered, setInventoryMovementsFiltered] =
    useState(null);
  const [inventoryMovementHeaderModel, setInventoryMovementHeaderModel] =
    useState();
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [suppliesFiltered, setSuppliesFiltered] = useState([]);

  const changeTab = (value) => {
    value >= 0 && setSelectedTab(value.toString());
  };

  const getTabPages = () => {
    const result = [
      {
        Id: "0",
        Name: localize.general_information,
      },
    ];

    if (model.Id > 0) {
      result.push({
        Id: "1",
        Name: localize.supplies,
      });

      result.push({
        Id: "2",
        Name: localize.inventory_movement_title,
      });
    }

    return result;
  };

  const getSupplies = async () => {
    const result = await axios.get(
      `${WAREHOUSE_ENDPOINT}/${model?.Id}/supplies`,
      {
        ...config,
        headers: {
          ...config.headers,
          pagination: -1,
        },
      }
    );

    setSupplies(result?.data);
    setSuppliesFiltered(result?.data?.Data);
    generateWarehouseSupplies(result?.data?.Data);
  };

  const generateWarehouseSupplies = (data) => {
    if (!data || data.length === 0) {
      return [localize.no_data, localize.no_data, localize.no_data];
    }

    return data.map((item) => generateSupplyData(item));
  };

  const generateSupplyData = (supply) => {
    const data = [
      supply.SupplyElement?.Code,
      supply.SupplyElement?.Name,
      supply.Quantity + "",
    ];
    return data;
  };

  const getInventoryMovements = async () => {
    const result = await axios.get(
      `${INVENTORY_MOVEMENT_HEADER_ENDPOINT}/warehouse/${model?.Id}`,
      {
        ...config,
        headers: {
          ...config.headers,
          pagination: -1,
        },
      }
    );

    setInventoryMovements(result?.data);
    setInventoryMovementsFiltered(result?.data?.Data);
  };

  useEffect(() => {
    getSupplies();
    getInventoryMovements();
  }, []);

  useEffect(() => {
    setSuppliesForPrinting &&
      setSuppliesForPrinting(generateWarehouseSupplies(suppliesFiltered));
  }, [suppliesFiltered]);

  return (
    <Box sx={{ width: "100%" }}>
      {showDetailModal && (
        <ModalBackground
          showModal={showDetailModal}
          setShowModal={setShowDetailModal}
          title={`${localize.movement} #${inventoryMovementHeaderModel.Id}`}
          hideSubmitButton
          onClickToCancel={() => {
            setShowDetailModal(false);
            setInventoryMovementHeaderModel(getClearInventoryMovement());
          }}
          cancelButtonText={localize.close}
          Component={
            <AddInventoryMovement
              model={inventoryMovementHeaderModel}
              setModel={setInventoryMovementHeaderModel}
            />
          }
        />
      )}

      <TabContext value={selectedTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <ScrollablePageTab
            selectedPage={parseInt(selectedTab)}
            setSelectedPage={changeTab}
            noMarginTop
            dynamicFormPages={getTabPages()}
          />
        </Box>

        <TabPanel value="0" index={0} sx={{ padding: "0px !important;" }}>
          <div
            style={{
              width: "100%",
              padding: "10px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "10px",
            }}
          >
            <AddOrEditBasicEntity
              nameMandatory
              model={model}
              setModel={setModel}
              customWidth={"100%"}
            />
          </div>
        </TabPanel>
        <TabPanel value="1" index={1} sx={{ padding: "0px !important;" }}>
          <Box className="flex flex-col gap-2 mt-3 w-full">
            {supplies && supplies?.Data?.length > 0 && (
              <SupplyFilter
                fullData={supplies?.Data}
                setDataFiltered={setSuppliesFiltered}
              />
            )}
            <List className="w-full flex flex-col gap-2">
              {suppliesFiltered && suppliesFiltered?.length > 0 ? (
                suppliesFiltered?.map((item) => (
                  <SupplyItem key={item.Id} item={item} />
                ))
              ) : (
                <NotificationItem
                  notification={{
                    message: localize.no_data,
                  }}
                />
              )}
            </List>
          </Box>
        </TabPanel>
        <TabPanel value="2" index={2} sx={{ padding: "0px !important;" }}>
          <Box className="flex flex-col gap-2 mt-2">
            {inventoryMovements?.Data?.length > 0 && (
              <InventoryMovementFilter
                fullData={inventoryMovements?.Data}
                setDataFiltered={setInventoryMovementsFiltered}
              />
            )}

            <List className="flex flex-col gap-2">
              {inventoryMovementsFiltered?.length > 0 ? (
                inventoryMovementsFiltered.map((movement) => (
                  <InventoryMovementItem
                    item={movement}
                    onOpen={() => {
                      setInventoryMovementHeaderModel(item);
                      setShowDetailModal(true);
                    }}
                  />
                ))
              ) : (
                <NotificationItem
                  notification={{
                    message: localize.no_data,
                  }}
                />
              )}
            </List>
          </Box>
        </TabPanel>
      </TabContext>
    </Box>
  );
}
